<template>
  <div id="pui-auth-page" class="pui-wrapper">
    <!-- This will "push" the form down -->
    <div class="push" />

    <pui-common-spinner v-if="loggingIn" message="" fullscreen hideBackgroundCircle />
    <!-- The submit handler can be piivo, because no other auth types have submit buttons -->
    <form @submit.prevent="submitPiivoLogin" class="login-form">
      <pui-image :src="imgLogoConnexion" class="logo" alt="PiiVO" />

      <span class="title">
        {{ useHardcodedMessages ? 'Sign in to PiiVO' : $t('platform.login.title') }}
      </span>

      <pui-error-message v-if="errorMessage" :message="errorMessage" picto="" />

      <template v-if="loadingExternalAuth">
        <pui-common-spinner></pui-common-spinner>
      </template>
      <template v-else-if="usePiivoAuth">
        <pui-input
          id="input-piivo-username"
          v-model="username"
          :placeholder="
            useHardcodedMessages ? 'Username' : $t('platform.login.username.placeholder')
          "
          :hasError="$v.username.$error"
          @input="$v.username.$touch()"
          name="login"
          type="text"
          class="form-item"
        >
          <pui-input-message
            :visible="$v.username.$error && !$v.username.required"
            :message="
              useHardcodedMessages ? 'Username required' : $t('platform.login.username.required')
            "
          />
        </pui-input>
        <pui-input
          id="input-piivo-password"
          v-model="password"
          :placeholder="
            useHardcodedMessages ? 'Password' : $t('platform.login.password.placeholder')
          "
          :hasError="$v.password.$error"
          @input="$v.password.$touch()"
          name="password"
          type="password"
          class="form-item"
        >
          <pui-input-message
            :visible="$v.password.$error && !$v.password.required"
            :message="
              useHardcodedMessages ? 'Password required' : $t('platform.login.password.required')
            "
          />
        </pui-input>
        <pui-button
          :id="`btn-submit-${LOGIN_TYPE_PIIVO}`"
          v-track:click="Tracking.AUTH_LOGIN"
          :disabled="$v.$invalid"
          variant="secondary"
          type="submit"
          block
          class="form-item"
        >
          {{ useHardcodedMessages ? 'Login with my PiiVO account' : $t('platform.login.confirm') }}
        </pui-button>
      </template>
      <template v-else-if="lastExternalAuth">
        <pui-button
          v-track:click.prevent="Tracking.AUTH_LOGIN_SSO"
          @click="submitOpenIdConnect(lastExternalAuth)"
          :id="`btn-submit-${lastExternalAuth.authenticationType}`"
          class="login-sso__btn form-item"
          variant="secondary"
          block
        >
          {{ lastExternalAuth.caption }}
        </pui-button>
      </template>

      <template v-if="!loadingExternalAuth && secondaryAuthentications.length">
        <i18n class="auth-types-separator form-item" path="platform.login.or"></i18n>

        <pui-theme-provider :theme="ssoTheme" class="secondary-auth-list">
          <pui-button
            v-for="(secondaryAuth, authIdx) in secondaryAuthentications"
            :key="authIdx"
            v-track:click.prevent="Tracking.AUTH_LOGIN_SSO"
            @click="submitSecondaryAuth(secondaryAuth)"
            :id="`btn-select-secondary-auth-${secondaryAuth.authenticationType}`"
            class="secondary-auth-btn form-item"
            variant="secondary"
            block
          >
            {{ secondaryAuth.caption }}
          </pui-button>
        </pui-theme-provider>
      </template>
    </form>

    <div class="bottom-container">
      <div v-if="loadingLanguages || languages.length > 1" class="languages-container">
        <i class="mdi mdi-earth globe-icon" />
        <pui-common-select
          :display="languageTranslate"
          :options="languages"
          :isLoading="loadingLanguages"
          :clearable="false"
          :multiple="false"
          :taggable="false"
          :value="selectedLanguageObj"
          :popperOptions="{ placement: 'top' }"
          @listUpdated="updateSelectedLanguage"
          class="language-select"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { required } from 'vuelidate/lib/validators';

import services from '../../core/services';
import { LanguagesManagerService } from '../../core/services/languagesManagerService';
import store from '../../core/store';
import { SET_LAST_LOGIN_TYPE } from '../../core/store/modules/coreModule';
import { LOGIN_TYPE_PIIVO } from '../constants';
import Tracking from '../constants/tracking';
import imgLogoConnexion from '../static/img/piivo-logo-connexion.svg';

export default {
  name: 'login',
  components: {},
  data() {
    return {
      // Expose tracking constants
      Tracking: Tracking,
      username: '',
      password: '',
      loggingIn: false,
      error: '',
      loadingExternalAuth: true,
      imgLogoConnexion,
      /**
       * @type {{ authenticationType:string; caption:string }[]}
       */
      externalAuthentications: [],
      ssoTheme: {
        'color-btn-secondary': '#f0f0f0',
        'color-btn-secondary-dark': '#dfdfdf',
        'color-initial': '#474747',
      },
      LOGIN_TYPE_PIIVO,
    };
  },
  computed: {
    /**
     * @returns {object[]} langauge choices
     */
    languages() {
      return [...LanguagesManagerService.getLanguages()].sort((a, b) =>
        this.languageTranslate(a).localeCompare(this.languageTranslate(b))
      );
    },
    /**
     * @returns {string} the current used locale
     */
    selectedLocale() {
      return LanguagesManagerService.getSelectedLocale();
    },
    /**
     * @returns {boolean} if loading the app language choices
     */
    loadingLanguages() {
      return LanguagesManagerService.getLoadingLanguages();
    },
    /**
     * @returns {string} an error message if the loading of the languages failed
     */
    loadLanguagesErrorMessage() {
      if (LanguagesManagerService.getLoadError()) {
        return this.useHardcodedMessages
          ? 'Error loading languages'
          : this.$t('platform.languages.load_error');
      }

      return '';
    },
    /**
     * @returns {object} The selected language object
     */
    selectedLanguageObj() {
      return this.languages.find(lang => lang.identifier === this.selectedLocale) || null;
    },
    /**
     * @returns {string} the error to display
     */
    errorMessage() {
      return this.loadLanguagesErrorMessage || this.error;
    },
    /**
     * @returns {boolean} if hardcoded messages should be used instead
     * of auto i18n
     */
    useHardcodedMessages() {
      return this.loadingLanguages || LanguagesManagerService.getLoadError();
    },
    /**
     * Getter and setter for the last login type identifier
     */
    lastLoginType: {
      get() {
        return store.getters.lastLoginType;
      },
      set(loginType) {
        store.commit(SET_LAST_LOGIN_TYPE, loginType);
      },
    },
    /**
     * @returns {object} the external auth object corresponding to 'lastLoginType'
     */
    lastExternalAuth() {
      if (!this.lastLoginType || this.lastLoginType === LOGIN_TYPE_PIIVO) {
        return null;
      }

      return this.externalAuthentications.find(
        auth => auth.authenticationType === this.lastLoginType
      );
    },
    /**
     * @returns {boolean} if the primary auth should be piivo
     */
    usePiivoAuth() {
      return (
        !this.lastLoginType || this.lastLoginType === LOGIN_TYPE_PIIVO || !this.lastExternalAuth
      );
    },
    /**
     * @returns {array} array of authentication methods that don't include
     * the one defined by 'lastLoginType'
     */
    secondaryAuthentications() {
      // If the main auth is piivo, the secondary auth types
      // are just the external auth
      if (this.usePiivoAuth) {
        return [...this.externalAuthentications];
      }

      // Otherwise the secondary auth types are...
      return [
        // First: piivo
        {
          authenticationType: LOGIN_TYPE_PIIVO,
          caption: this.useHardcodedMessages
            ? 'Login with PiiVO'
            : this.$t('platform.login.select_auth_type_piivo'),
        },
        // And then the external auths (excluding the selected ext auth)
        ...this.externalAuthentications.filter(
          auth => auth.authenticationType !== this.lastExternalAuth.authenticationType
        ),
      ];
    },
  },
  methods: {
    /**
     * @returns {string} the native label of the language object
     */
    languageTranslate(value) {
      return value.label[value.identifier] || '';
    },
    piivoTranslate(value) {
      return Vue.filter('piivoTranslate')(value);
    },
    submitPiivoLogin() {
      if (this.$v.$invalid) {
        return;
      }

      this.lastLoginType = LOGIN_TYPE_PIIVO;

      this.loggingIn = true;
      this.error = '';
      const credentials = {
        username: this.username,
        password: this.password,
      };

      // Auth.login() returns the response message.
      // A redirect will happen on success.
      const redirect = this.$route.query.redirect || '/';
      services
        .getService('auth')
        .login(credentials, redirect)
        .then(response => {
          this.loggingIn = false;
          this.error = response;
        });
    },
    /**
     * Triggers login method of the secondary auth
     *
     * @param {object} secondaryAuth - the auth object
     */
    submitSecondaryAuth(secondaryAuth) {
      if (secondaryAuth.authenticationType === LOGIN_TYPE_PIIVO) {
        // If the user selects piivo auth, just set it as the last login type
        // That will automatically display the login form
        this.lastLoginType = LOGIN_TYPE_PIIVO;
      } else {
        // Otherwise, the auth is an external auth type
        // Trigger the login with that specific method
        this.submitOpenIdConnect(secondaryAuth);
      }
    },
    /**
     * Launch external authentication by redirecting to the
     * external login
     *
     * @param externalAuth - the selected external auth
     */
    submitOpenIdConnect(externalAuth) {
      this.lastLoginType = externalAuth.authenticationType;

      // Redirect to external signin for OpenId
      const redirect = this.$route.query.redirect || '/';
      window.location.href = services
        .getService('auth')
        .getExternalSigninUrl(externalAuth.authenticationType, redirect);
    },
    /**
     * Updates the UI language
     *
     * @param {object[]} languages
     */
    updateSelectedLanguage(languages) {
      if (Array.isArray(languages) && languages.length) {
        const lang = languages[0];
        LanguagesManagerService.setSelectedLocale(lang.identifier);
      }
    },
  },
  /**
   * Mounted hook.
   */
  async mounted() {
    // Initialize error message
    if (this.$route.query.error === 'EXT_SIGNIN') {
      // Show external signin error if present
      this.error = this.$t('platform.login.error.external_signin');
    }

    this.loadingExternalAuth = true;

    try {
      // Get enabled external authentication types
      this.externalAuthentications = await services
        .getService('auth')
        .getExternalAuthenticationTypes();
    } catch (err) {
      this.externalAuthentications = [];
    }

    this.loadingExternalAuth = false;
  },
  validations: {
    username: {
      required,
    },
    password: {
      required,
    },
  },
};
</script>
